// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Group } from './parties.ts';

export interface Candidate {
  surname: string;
  given: string;
  party: string;
  partyName: string;
  group: Group;
  votes: number;
  pct: number;      // percentage within its own vote-type column (raw AEC)
  totalPct: number; // share of the seat's formal first-preference total
  swing: number;
  elected: boolean;
}

export interface TcpEntry {
  surname: string;
  given: string;
  party: string;
  partyName: string;
  group: Group;
  votes: number;
  pct: number;
  swing: number;
  elected: boolean;
}

export interface Member {
  given: string;
  surname: string;
  party: string;
  partyName: string;
  group: Group;
}

export interface Seat {
  id: number;
  name: string;
  state: string;
  member: Member;
  winnerGroup: Group;
  runnerGroup: Group;
  tcp: TcpEntry[];
  candidates: Candidate[];
  tppALP: number;
  tppLNP: number;
  tppALPPct: number;
  tppLNPPct: number;
  tppTotal: number;
  tppSwingToALP: number | null; // null where the seat was non-classic in 2022 (no comparable prior)
  tppLeader: 'ALP' | 'COA';
  tppMargin: number;   // |tppALPPct - 50|
  marginPct: number;   // actual TCP winner margin over 50
  nonClassic: boolean;
  changedHands: boolean;
  prevParty: string | null;
  prevGroup: Group | null;
  enrolment: number;
  turnout: number;
  turnoutPct: number;
  turnoutSwing: number;
  formalVotes: number;
  informalVotes: number;
  informalPct: number;
  informalSwing: number;
}

export interface PartyRow {
  ab: string;
  name: string;
  group: Group;
  seats: number;
  contested: number;
  votes: number;
  pct: number;
  swing: number;
}

export interface FlowRow {
  ab: string;
  name: string;
  group: Group;
  toALP: number;
  toALPPct: number;
  toCOA: number;
  toCOAPct: number;
  total: number;
}

export interface StateFirstPref {
  state: string;
  total: number;
  groups: Partial<Record<Group, number>>;
}

export interface National {
  tppALP: number;
  tppLNP: number;
  tppALPPct: number;
  tppLNPPct: number;
  tppSwingToALP: number;
  enrolment: number;
  turnout: number;
  turnoutPct: number;
  formalVotes: number;
  informalVotes: number;
  informalPct: number;
  seatsByParty: Record<string, number>;
  seatsByGroup: Record<Group, number>;
  coalitionSeats: number;
  noSwingCount: number;
}

export interface ElectionData {
  event: string;
  eventName: string;
  generated: string;
  totalSeats: number;
  national: National;
  parties: PartyRow[];
  stateFirstPrefs: StateFirstPref[];
  flows: FlowRow[];
  seats: Seat[];
}

export const STATE_NAMES: Record<string, string> = {
  NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland', WA: 'Western Australia',
  SA: 'South Australia', TAS: 'Tasmania', ACT: 'Australian Capital Territory', NT: 'Northern Territory',
};

let cache: ElectionData | null = null;

export async function loadElection(): Promise<ElectionData> {
  if (cache) return cache;
  const res = await fetch('data/election.json');
  if (!res.ok) throw new Error(`Could not load election data (HTTP ${res.status})`);
  cache = (await res.json()) as ElectionData;
  return cache;
}
